/* eslint-disable max-len */
/* eslint-disable vue/max-len */

import { create } from '@bufbuild/protobuf';

import * as pbm from '@/grpc-gen/marketing_campaign_pb';
import * as pbs from '@/grpc-gen/marketing_campaign_manager_service_pb';
import { DateRange, dateRangeFromPeriod, DateRangePeriod } from '@/services/date-range';
import ContactFilter from './contact-filter';

export enum MarketingCampaignStatus {
  None = 'none',
  Draft = 'draft',
  Scheduled = 'scheduled',
  Processing = 'processing',
  Sent = 'sent',
  Abandoned = 'abandoned',
  Failure = 'failure'
}

export interface MarketingCampaignMapping {
  marketingCampaignManagerContactFilters: ContactFilter[];
}

export default class MarketingCampaign {
  id: number = 0;

  locale?: string | null;

  title?: string | null;

  subject?: string | null;

  previewText?: string | null;

  contactFilter: ContactFilter | null = null;

  templateName?: string | null;

  templateDesign?: string | null;

  templateHtml?: string | null;

  templatePlaintext?: string | null;

  templatePreview?: string | null;

  status?: MarketingCampaignStatus = MarketingCampaignStatus.Draft;

  dtCreate?: Date;

  dtUpdate?: Date;

  dtSend?: Date | null;

  dtProcessed?: Date | null;

  isArchived?: boolean;

  isDeleted?: boolean;

  audienceStats?: {
    matching?: number;
    all?: number;
  };

  publishStats?: {
    scheduled?: number;
    sent?: number;
    processing?: number;
    abandoned?: number;
    failure?: number;
    all?: number;
  };

  deliveryStats?: {
    processed? : number;
    dropped? : number;
    delivered? : number;
    deferred? : number;
    bounce? : number;
    blocked? : number;
  };

  engagementStats?: {
    open?: number;
    unique_open?: number;
    click?: number;
    unique_click?: number;
    spam?: number;
    unsubscribe?: number;
    group_unsubscribe?: number;
    group_resubscribe?: number;

    widget_open?: number;
    unique_widget_open?: number;
    widget_reservation?: number;
    unique_widget_reservation?: number;
    widget_unsubscribe?: number;

  };

  conversionStats?: {
    all?: number;
    imminent?: number;
    unique_all?: number;
    unique_imminent?: number;
    guests_all?: number;
    guests_imminent?: number;
  };

  get date(): Date { return this.dtSend ?? this.dtCreate!; }

  get canArchive(): boolean { return [MarketingCampaignStatus.Sent, MarketingCampaignStatus.Abandoned, MarketingCampaignStatus.Failure].includes(this.status!); }

  get canDelete(): boolean { return [MarketingCampaignStatus.Draft].includes(this.status!); }

  get recipientCount(): number | undefined { return [MarketingCampaignStatus.Draft].includes(this.status!) ? this.audienceStats?.matching : this.publishStats?.sent; }

  static fromGrpcResponse(r: pbs.MarketingCampaignResponse, m?: MarketingCampaignMapping): MarketingCampaign {
    return MarketingCampaign.fromGrpcModel(r.marketingCampaign, m);
  }

  static fromGrpcModel(o?: pbm.MarketingCampaign, m?: MarketingCampaignMapping): MarketingCampaign {
    const statuses = [
      MarketingCampaignStatus.None,
      MarketingCampaignStatus.Draft,
      MarketingCampaignStatus.Scheduled,
      MarketingCampaignStatus.Processing,
      MarketingCampaignStatus.Sent,
      MarketingCampaignStatus.Abandoned,
      MarketingCampaignStatus.Failure,
    ];

    const mc = new MarketingCampaign();

    mc.id = Number(o?.id ?? 0);
    mc.locale = o?.locale ?? null;
    mc.status = statuses[o?.status ?? 0];
    mc.title = o?.title ?? null;
    mc.subject = o?.subject ?? null;
    mc.previewText = o?.previewText ?? null;
    mc.contactFilter = m?.marketingCampaignManagerContactFilters.find((cf) => cf.id === Number(o?.contactFilterId ?? 0)) ?? null;
    mc.templateName = o?.templateName ?? null;
    mc.templateDesign = o?.templateDesign ?? null;
    mc.templateHtml = o?.templateHtml ?? null;
    mc.templatePlaintext = o?.templatePlaintext ?? null;
    mc.templatePreview = o?.templatePreview ?? null;
    mc.dtCreate = o?.dtCreate ? new Date(Number(o.dtCreate) * 1000) : new Date();
    mc.dtUpdate = o?.dtUpdate ? new Date(Number(o.dtUpdate) * 1000) : new Date();
    mc.dtSend = o?.dtSend ? new Date(Number(o.dtSend) * 1000) : null;
    mc.dtProcessed = o?.dtProcessed ? new Date(Number(o.dtProcessed) * 1000) : null;
    mc.isArchived = o?.isArchived || undefined;
    mc.isDeleted = o?.isDeleted || undefined;

    const astats = o?.audienceStats;
    if (astats) {
      mc.audienceStats = {
        all: Number(astats.all),
        matching: Number(astats.matching),
      };
    }

    const pstats = o?.publishStats;
    if (pstats) {
      mc.publishStats = {
        all: Number(pstats.all),
        scheduled: Number(pstats.scheduled),
        sent: Number(pstats.sent),
        processing: Number(pstats.processing),
        abandoned: Number(pstats.abandoned),
        failure: Number(pstats.failure),
      };
    }

    const dstats = o?.deliveryStats;
    if (dstats) {
      mc.deliveryStats = {
        processed: Number(dstats.processed),
        dropped: Number(dstats.dropped),
        delivered: Number(dstats.delivered),
        deferred: Number(dstats.deferred),
        bounce: Number(dstats.bounce),
        blocked: Number(dstats.blocked),
      };
    }

    const estats = o?.engagementStats;
    if (estats) {
      mc.engagementStats = {
        open: Number(estats.open),
        unique_open: Number(estats.uniqueOpen),
        click: Number(estats.click),
        unique_click: Number(estats.uniqueClick),
        spam: Number(estats.spam),
        unsubscribe: Number(estats.unsubscribe),
        group_unsubscribe: Number(estats.groupUnsubscribe),
        group_resubscribe: Number(estats.groupResubscribe),

        widget_open: Number(estats.widgetOpen),
        unique_widget_open: Number(estats.uniqueWidgetOpen),
        widget_reservation: Number(estats.widgetReservation),
        unique_widget_reservation: Number(estats.uniqueWidgetReservation),
        widget_unsubscribe: Number(estats.widgetUnsubscribe),
      };
    }

    const cstats = o?.conversionStats;
    if (cstats) {
      mc.conversionStats = {
        all: Number(cstats.all),
        imminent: Number(cstats.imminent),
        unique_all: Number(cstats.uniqueAll),
        unique_imminent: Number(cstats.uniqueImminent),
        guests_all: Number(cstats.guestsAll),
        guests_imminent: Number(cstats.guestsImminent),
      };
    }

    return mc;
  }

  toGrpcModel(): pbm.MarketingCampaign {
    return create(pbm.MarketingCampaignSchema, {
      id: BigInt(this.id),
      locale: this.locale ?? '',
      title: this.title ?? '',
      subject: this.subject ?? '',
      previewText: this.previewText ?? '',
      contactFilterId: BigInt(this.contactFilter?.id ?? 0),
      contactFilterName: this.contactFilter?.name ?? '',
      contactFilterData: this.contactFilter?.data ?? '',
      templateName: this.templateName ?? '',
      templateDesign: this.templateDesign ?? '',
      templateHtml: this.templateHtml ?? '',
      templatePlaintext: this.templatePlaintext ?? '',
      templatePreview: this.templatePreview ?? '',
      status: pbm.MarketingCampaign_Status.DRAFT,
      dtCreate: 0n,
      dtUpdate: 0n,
      dtSend: 0n,
      dtProcessed: 0n,
      isArchived: this.isArchived ?? false,
      isDeleted: this.isDeleted ?? false,
    });
  }

  static compare(o1: MarketingCampaign, o2: MarketingCampaign): number {
    const d1 = o1.dtSend ?? o1.dtCreate;
    const d2 = o2.dtSend ?? o2.dtCreate;

    const res = d2!.getTime() - d1!.getTime();
    if (res !== 0) return res;
    return o2.id - o1.id;
  }

  clone(): MarketingCampaign {
    const copy = new MarketingCampaign();
    Object.assign(copy, this);
    return copy;
  }
}

export interface MarketingCampaignListParams {
  beginDateIndex: number,
  endDateIndex: number,
  showArchived: boolean,
}

export interface MarketingCampaignFilter {
  dateRange: DateRange;

  showArchived: boolean
}

export function defaultMarketingCampaignFilter(): MarketingCampaignFilter {
  return {
    dateRange: dateRangeFromPeriod(DateRangePeriod.Last90Days),
    showArchived: false,
  };
}

export function areMarketingCampaignFiltersSame(o1: MarketingCampaignFilter, o2: MarketingCampaignFilter): boolean {
  if (o1.dateRange.beginDate.getTime() !== o2.dateRange.beginDate.getTime()) return false;
  if (o1.dateRange.endDate.getTime() !== o2.dateRange.endDate.getTime()) return false;
  if (o1.showArchived !== o2.showArchived) return false;
  return true;
}
